import type { CSSProperties } from 'react';
import ContentEditable from '../content-editable';

export type ButtonWidgetSize = 'small' | 'medium' | 'large';

const SIZE_PRESETS: Record<ButtonWidgetSize, { height: string; fontSize: string; buttonPadding: string }> = {
  small: { height: '40px', fontSize: '13px', buttonPadding: '0 20px' },
  medium: { height: '48px', fontSize: '16px', buttonPadding: '0 28px' },
  large: { height: '56px', fontSize: '18px', buttonPadding: '0 36px' },
};

export interface ButtonWidgetProps {
  blockId?: string; // lets the editor find this rendered node in the DOM
  value: string | undefined;
  setValue?: (str: string) => void;
  href?: string;
  size?: ButtonWidgetSize;
  background?: string;
  color?: string;
  border?: string;
  borderStyle?: CSSProperties['borderStyle'];
  borderColor?: string;
  fontWeight?: CSSProperties['fontWeight'];
  buttonColor?: string;
  align?: 'left' | 'center' | 'right';
  borderRadius?: string | number;
  padding?: string;
  fontFamily?: string;
  letterSpacing?: string;
  textTransform?: CSSProperties['textTransform'];
  /** renders as a full-width bar (button fills the content column) instead of a shrink-wrapped
   * pill - some templates (amalfi, florence, chateau, laurent) use this bar style for their CTA.
   * purely controls the outer table's width; sizing mechanism is independent (see `tdPadding`). */
  fullWidth?: boolean;
  /** fixed height for the button box - if omitted alongside `tdPadding`, sizes naturally from
   * padding + text instead of a small/medium/large size preset height */
  height?: string;
  /** padding on the colored td itself (button sizes naturally from this + the text, no fixed
   * height) - an alternative to the default fixed-height + inner `<a>`-padding sizing */
  tdPadding?: string;
  fontSize?: string;
  /** inner `<a>` padding for the default fixed-height sizing - defaults to the `size` preset's value */
  buttonPadding?: string;
  /** text line-height - defaults to the button's own `height`, but some templates hand-tune a
   * slightly smaller value than the box height to vertically center text inside a bordered button */
  lineHeight?: string;
}

export default function ButtonWidget({
  blockId,
  value,
  setValue,
  href = '#',
  size = 'medium',
  background,
  color,
  border,
  borderStyle,
  borderColor,
  fontWeight = 600,
  buttonColor,
  align = 'center',
  borderRadius,
  padding = '20px 56px',
  fontFamily,
  letterSpacing,
  textTransform,
  fullWidth = false,
  height: customHeight,
  tdPadding, //sizes the button naturally from padding + text - no fixed height unless one's explicitly given
  fontSize: customFontSize,
  buttonPadding: customButtonPadding,
  lineHeight: customLineHeight,
}: ButtonWidgetProps) {
  const sizePreset = SIZE_PRESETS[size];
  const height = tdPadding ? customHeight : (customHeight ?? sizePreset.height);
  const fontSize = customFontSize ?? sizePreset.fontSize;
  const buttonPadding = customButtonPadding ?? sizePreset.buttonPadding;
  const lineHeight = customLineHeight ?? height;

  const buttonTd = tdPadding ? (
    <td
      align="center"
      valign="middle"
      style={{
        background: buttonColor,
        border: border ? `${border} ${borderStyle ?? 'solid'} ${borderColor ?? 'transparent'}` : undefined,
        borderRadius,
        height,
        padding: tdPadding,
      }}
    >
      <a href={href} onClick={(e) => e.preventDefault()} style={{ textDecoration: 'none', display: 'inline-block' }}>
        <ContentEditable
          value={value}
          setValue={setValue}
          style={{
            color,
            fontFamily,
            fontSize,
            fontWeight,
            lineHeight,
            letterSpacing,
            textTransform,
            wordWrap: 'break-word',
            whiteSpace: 'nowrap',
          }}
        />
      </a>
    </td>
  ) : (
    <td
      align="center"
      valign="middle"
      style={{
        background: buttonColor,
        border: border ? `${border} ${borderStyle ?? 'solid'} ${borderColor ?? 'transparent'}` : undefined,
        borderRadius,
        height,
      }}
    >
      <a
        href={href}
        onClick={(e) => e.preventDefault()}
        style={{
          textDecoration: 'none',
          display: 'inline-block',
          padding: buttonPadding,
          height: '100%',
          textAlign: 'center',
        }}
      >
        <ContentEditable
          value={value}
          setValue={setValue}
          style={{
            color,
            fontFamily,
            fontSize,
            fontWeight,
            lineHeight,
            letterSpacing,
            textTransform,
            wordWrap: 'break-word',
            whiteSpace: 'nowrap',
          }}
        />
      </a>
    </td>
  );

  return (
    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0} style={{ background }}>
      <tbody>
        <tr>
          <td data-block-id={blockId} align={align} style={{ padding }}>
            {fullWidth ? (
              <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                <tbody>
                  <tr>{buttonTd}</tr>
                </tbody>
              </table>
            ) : (
              <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                <tbody>
                  <tr>{buttonTd}</tr>
                </tbody>
              </table>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
